import React, { useState } from 'react';
import {
    Box, Button, Chip, Grid, TextField, Typography, CircularProgress, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { Assessment as IndicateurIcon, Visibility as ViewIcon, Close as CloseIcon } from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import { academyApi } from '../services/academyApi';
import { useLanguage } from '../../Commun/contexts/LanguageContext';

interface MailLogRow {
    EML_IDF: number;
    EML_TO: string;
    EML_CC: string | null;
    EML_SUBJECT: string;
    EML_BODY: string | null;
    EML_IS_HTML: boolean | number;
    EML_STATUS: string;
    EML_TYPE?: string | null;
    EML_ERROR_MESSAGE: string | null;
    EML_SMTP_HOST: string | null;
    EML_SMTP_PORT: number | string | null;
    EML_SENT_AT: string;
}

// boubaker 25/08/2026 - Page Indicateur : Traçabilité des envois mail
// (GenericPage type 'indicateur' : filtres dates + type + Load + tableau résultats)
export const IndicateurMailLogPage: React.FC = () => {
    const { t } = useLanguage();

    const todayStr = new Date().toISOString().slice(0, 10);
    const monthStart = todayStr.slice(0, 8) + '01';
    const [dateFrom, setDateFrom] = useState(monthStart);
    const [dateTo, setDateTo] = useState(todayStr);
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [typeFilter, setTypeFilter] = useState<string>('all');
    const [toFilter, setToFilter] = useState('');

    const [logs, setLogs] = useState<MailLogRow[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [loading, setLoading] = useState(false);

    // Body preview dialog
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewSubject, setPreviewSubject] = useState('');
    const [previewBody, setPreviewBody] = useState('');
    const [previewType, setPreviewType] = useState('');
    const [previewIsHtml, setPreviewIsHtml] = useState(true);

    const handleLoad = async () => {
        setLoading(true);
        try {
            const res = await academyApi.getMailLogs(dateFrom, dateTo, {
                status: statusFilter !== 'all' ? statusFilter : undefined,
                type: typeFilter !== 'all' ? typeFilter : undefined,
                to: toFilter || undefined,
            });
            setLogs(res || []);
            setLoaded(true);
        } finally {
            setLoading(false);
        }
    };

    const handlePreview = (row: MailLogRow) => {
        setPreviewSubject(row.EML_SUBJECT);
        setPreviewBody(row.EML_BODY || '');
        setPreviewType(row.EML_TYPE || 'GENERAL');
        // boubaker 25/08/2026 - BIT SQL → boolean true/false OU number 1/0
        setPreviewIsHtml(!!row.EML_IS_HTML);
        setPreviewOpen(true);
    };

    const statusColor = (s: string) => {
        switch (s) {
            case 'SENT': return 'success';
            case 'FAILED': return 'error';
            case 'PENDING': return 'warning';
            default: return 'default';
        }
    };

    const getMailTypeLabel = (type?: string | null) => {
        switch (type) {
            case 'DEBT_REMINDER': return t('academy.indicateur.mail_type.debt_reminder');
            case 'AUTH_OTP':
            case 'DOUBLE_AUTH': return t('academy.indicateur.mail_type.auth_otp');
            case 'CHANGE_PASSWORD': return t('academy.indicateur.mail_type.change_password');
            case 'RESET_PASSWORD': return t('academy.indicateur.mail_type.reset_password');
            case 'GENERAL': return t('academy.indicateur.mail_type.general');
            default: return type || t('academy.indicateur.mail_type.general');
        }
    };

    const getMailTypeColor = (type?: string | null): 'secondary' | 'info' | 'warning' | 'default' | 'primary' => {
        switch (type) {
            case 'DEBT_REMINDER': return 'secondary';
            case 'AUTH_OTP':
            case 'DOUBLE_AUTH': return 'info';
            case 'CHANGE_PASSWORD':
            case 'RESET_PASSWORD': return 'warning';
            default: return 'default';
        }
    };

    const filters = (
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
                <TextField type="date" fullWidth size="small" label={t('academy.scheduler.start_date')} value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
                <TextField type="date" fullWidth size="small" label={t('academy.scheduler.end_date')} value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
                <TextField select fullWidth size="small" label={t('academy.indicateur.mail.status')} value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)} SelectProps={{ native: true }}>
                    <option value="all">{t('academy.print.status_all')}</option>
                    <option value="SENT">{t('academy.indicateur.status.sent')}</option>
                    <option value="FAILED">{t('academy.indicateur.status.failed')}</option>
                    <option value="PENDING">{t('academy.indicateur.status.pending')}</option>
                </TextField>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
                <TextField select fullWidth size="small" label={t('academy.indicateur.mail.type_filter')} value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)} SelectProps={{ native: true }}>
                    <option value="all">{t('academy.indicateur.mail_type.all')}</option>
                    <option value="DEBT_REMINDER">{t('academy.indicateur.mail_type.debt_reminder')}</option>
                    <option value="AUTH_OTP">{t('academy.indicateur.mail_type.auth_otp')}</option>
                    <option value="CHANGE_PASSWORD">{t('academy.indicateur.mail_type.change_password')}</option>
                    <option value="RESET_PASSWORD">{t('academy.indicateur.mail_type.reset_password')}</option>
                    <option value="GENERAL">{t('academy.indicateur.mail_type.general')}</option>
                </TextField>
            </Grid>
            <Grid item xs={12} sm={4} md={2.8}>
                <TextField fullWidth size="small" label={t('academy.indicateur.mail.to_filter')} value={toFilter}
                    onChange={(e) => setToFilter(e.target.value)} placeholder="email@..." />
            </Grid>
            <Grid item xs={12} md={1.2}>
                <Button variant="contained" color="primary" fullWidth disabled={loading} onClick={handleLoad}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}>
                    {t('academy.scheduler.load')}
                </Button>
            </Grid>
        </Grid>
    );

    const cellStyle = (width?: number, extra?: React.CSSProperties): React.CSSProperties =>
        ({ border: '1px solid #999', padding: '4px 6px', textAlign: 'left', verticalAlign: 'top', width, ...extra });

    return (
        <GenericPage
            title={t('academy.indicateur.mail_log')}
            icon={<IndicateurIcon />}
            type="indicateur"
            filters={filters}
            footerEnabled={false}
        >
            {!loaded ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                    {t('academy.scheduler.not_loaded')}
                </Typography>
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f0f0f0' }}>
                                <th style={cellStyle(80)}>{t('academy.indicateur.col_date')}</th>
                                <th style={cellStyle(90)}>{t('academy.indicateur.col_type')}</th>
                                <th style={cellStyle(160)}>{t('academy.indicateur.col_to')}</th>
                                <th style={cellStyle(180)}>{t('academy.indicateur.col_subject')}</th>
                                <th style={cellStyle(65)}>{t('academy.indicateur.col_status')}</th>
                                <th style={cellStyle(110)}>{t('academy.indicateur.col_smtp')}</th>
                                <th style={cellStyle(50)}>{t('academy.indicateur.col_action')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.length === 0 ? (
                                <tr><td colSpan={7} style={{ ...cellStyle(), textAlign: 'center' }}>{t('academy.print.no_data')}</td></tr>
                            ) : logs.map(row => (
                                <tr key={row.EML_IDF}>
                                    <td style={cellStyle(80)}>
                                        {row.EML_SENT_AT ? new Date(row.EML_SENT_AT).toLocaleString() : ''}
                                    </td>
                                    <td style={cellStyle(90)}>
                                        <Chip
                                            label={getMailTypeLabel(row.EML_TYPE)}
                                            color={getMailTypeColor(row.EML_TYPE)}
                                            size="small"
                                            variant="filled"
                                            sx={{ fontSize: '0.65rem', height: 20, fontWeight: 'bold' }}
                                        />
                                    </td>
                                    <td style={cellStyle(160)}>{row.EML_TO}</td>
                                    <td style={cellStyle(180)}>
                                        <Tooltip title={t('academy.indicateur.view_body')}>
                                            <IconButton size="small" onClick={() => handlePreview(row)}>
                                                <ViewIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        {row.EML_SUBJECT}
                                    </td>
                                    <td style={cellStyle(65)}>
                                        <Chip label={row.EML_STATUS} color={statusColor(row.EML_STATUS) as any} size="small" variant="outlined" />
                                        {row.EML_ERROR_MESSAGE && (
                                            <Typography variant="caption" display="block" color="error" sx={{ mt: 0.5, fontSize: 9 }}>
                                                {row.EML_ERROR_MESSAGE.length > 80 ? row.EML_ERROR_MESSAGE.slice(0, 80) + '…' : row.EML_ERROR_MESSAGE}
                                            </Typography>
                                        )}
                                    </td>
                                    <td style={cellStyle(110)}>
                                        {row.EML_SMTP_HOST || '-'}:{row.EML_SMTP_PORT || '-'}
                                    </td>
                                    <td style={cellStyle(50)}>
                                        <Tooltip title={t('academy.indicateur.view_body')}>
                                            <IconButton size="small" onClick={() => handlePreview(row)}>
                                                <ViewIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Box>
            )}

            {/* boubaker 25/08/2026 - Dialog aperçu du corps du mail */}
            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">{previewSubject}</Typography>
                        <Chip
                            label={getMailTypeLabel(previewType)}
                            color={getMailTypeColor(previewType)}
                            size="small"
                            sx={{ fontSize: '0.7rem', height: 22, fontWeight: 'bold' }}
                        />
                    </Box>
                    <IconButton onClick={() => setPreviewOpen(false)} size="small"><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {previewIsHtml ? (
                        <Box
                            component="iframe"
                            srcDoc={previewBody}
                            sx={{ width: '100%', minHeight: 400, border: '1px solid #ddd', borderRadius: 1, bgcolor: '#fff' }}
                            sandbox="allow-same-origin"
                            title={previewSubject}
                        />
                    ) : (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{previewBody}</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewOpen(false)}>{t('common.actions.close')}</Button>
                </DialogActions>
            </Dialog>
        </GenericPage>
    );
};

export default IndicateurMailLogPage;
